/**
 * The rules of a voice sample (ADR-0062), in one place: how much of the subject's speech it asks
 * for, how little it may be kept with, how long it may run, and what the subject is asked to talk
 * about. The screens and the recording session both read them from here. Pure.
 *
 * The speech is counted in the browser from the microphone's level — an estimate. The server
 * measures the audio itself and makes no voiceprint of less than its own minimum (10 s), so the
 * minimum recording length here leaves a margin over that. A low browser estimate warns but
 * does not prevent the server from checking a long enough recording. Before the count, the room
 * is listened to; while it counts, the level says when the voice is too faint or the sound clips.
 * Those thresholds are at the end, and provisional.
 */
import type { VoiceRoom, VoiceTakeSummary } from './voice-types'

/** The speech a sample asks for: the recording stops by itself once this much was counted. */
export const VOICE_SAMPLE_TARGET_SPEECH_MS = 30_000

/** Minimum recommended speech, and minimum recording length before server validation. */
export const VOICE_SAMPLE_MIN_SPEECH_MS = 15_000

/** The longest a sample records, whatever was said: it stops by itself there. */
export const VOICE_SAMPLE_MAX_ELAPSED_MS = 120_000

/** How long one topic stays on the screen before the next takes its place. */
export const VOICE_TOPIC_ROTATION_MS = 8_000

/**
 * What the subject is asked to talk about, in the order shown: neutral everyday things, never
 * the case. The words are `humint.voice.sample.topics.<topic>`.
 */
export const VOICE_TOPICS = ['journey', 'home', 'yesterday', 'work', 'meal'] as const

export type VoiceTopic = (typeof VOICE_TOPICS)[number]

/** The topic shown this far into a recording: one at a time, round and round. */
export function voiceTopicAt(elapsedMs: number): VoiceTopic {
  const turn = Math.floor(Math.max(0, elapsedMs) / VOICE_TOPIC_ROTATION_MS)
  return VOICE_TOPICS[turn % VOICE_TOPICS.length]
}

/** Whether the browser estimate meets the recommended minimum speech duration. */
export const isEnoughSpeech = (speechMs: number): boolean => speechMs >= VOICE_SAMPLE_MIN_SPEECH_MS

/** Files are checked by the server; recorded takes must be long enough to contain useful speech. */
export const canKeepVoiceSample = (
  take: Pick<VoiceTakeSummary, 'durationMs' | 'fileName'>
): boolean => Boolean(take.fileName) || take.durationMs >= VOICE_SAMPLE_MIN_SPEECH_MS

/** Whether the sample holds all the speech it asks for. */
export const isTargetReached = (speechMs: number): boolean =>
  speechMs >= VOICE_SAMPLE_TARGET_SPEECH_MS

/**
 * How long a silence lasts before it is announced to a screen reader. The caption a sighted
 * operator sees follows the voice at once; said aloud at every breath it would talk over the
 * subject.
 */
export const VOICE_SILENCE_ANNOUNCE_MS = 3_000

// ── The room check and the guidance on loudness ──────────────────────────────
//
// PROVISIONAL, every number below: chosen from what the server's own rules imply for a voice at
// its usual level on a microphone, and never measured on a real phone. They want tuning on real
// recordings, with the server's thresholds
// (`QualityRule`). Levels are dB under full scale (dBFS), as `level-meter.ts` reads a frame.

/** For how long the room is listened to before the speech is counted. */
export const VOICE_ROOM_CHECK_MS = 2_000

/** A room whose noise stays under this is quiet. Provisional. */
export const VOICE_ROOM_QUIET_BELOW_DBFS = -50

/** A room whose noise reaches this is too loud for a sample; between the two, *some noise*. Provisional. */
export const VOICE_ROOM_LOUD_FROM_DBFS = -38

/** What a room with this much noise is called. */
export function roomOf(noiseDbfs: number): VoiceRoom {
  if (noiseDbfs < VOICE_ROOM_QUIET_BELOW_DBFS) return 'quiet'
  return noiseDbfs < VOICE_ROOM_LOUD_FROM_DBFS ? 'noisy' : 'loud'
}

/**
 * For how long the caption says what the room check found once the counting has started, unless
 * a voice is heard sooner.
 */
export const VOICE_ROOM_SAID_MS = 4_000

/**
 * A voice no frame of which reaches this level is too faint: the subject is asked to come closer
 * or speak up. Provisional.
 */
export const VOICE_FAINT_BELOW_DBFS = -34

/** A sound this far over the room's noise is taken for a voice, however faint. Provisional. */
export const VOICE_HEARD_OVER_ROOM_DB = 6

/** The room's noise where the check could not tell it: a quiet room. Provisional. */
export const VOICE_ASSUMED_ROOM_DBFS = -60

/** A voice too faint, or a sound that clips, is said once it has lasted this long… */
export const VOICE_GUIDANCE_AFTER_MS = 2_000

/** …and no longer said once the sound has been free of clipping, or of any voice, for this long. */
export const VOICE_GUIDANCE_CLEAR_MS = 1_500

/** Clipped frames no further apart than this are one stretch of clipping. */
export const VOICE_CLIPPING_GAP_MS = 1_000

/**
 * A screen reader is told the same guidance again no sooner than this: what the eye reads in the
 * caption at once would, said aloud each time, talk over the subject.
 */
export const VOICE_GUIDANCE_REPEAT_MS = 10_000
